import type { PostgrestResponse } from '@supabase/supabase-js';

/**
 * PostgREST caps every response at the project's `max_rows` setting (1000 on
 * our Supabase projects) and returns the truncated page with a 200 status —
 * there is no error to catch. Any query whose result feeds a computation over
 * *all* rows (balances, statistics, budgets, import de-duplication) must page
 * through the result set instead of awaiting the builder directly.
 */
export const POSTGREST_MAX_ROWS = 1000;

/** The subset of the PostgREST builder API `fetchAllRows` needs. */
interface PagedQuery<Row> extends PromiseLike<PostgrestResponse<Row>> {
  order(column: string, options?: { ascending?: boolean }): PagedQuery<Row>;
  range(from: number, to: number): PagedQuery<Row>;
}

/**
 * Fetch every row matching `query`, transparently paging past `max_rows`.
 *
 * `orderColumn` is appended as the final sort key so consecutive pages are
 * stable even when the caller's own `.order()` has ties. Throws on the first
 * PostgREST error.
 */
export async function fetchAllRows<Row>(
  query: PagedQuery<Row>,
  orderColumn = 'id',
): Promise<Row[]> {
  // The builder mutates in place, so the tiebreaker is added once, outside the loop.
  const ordered = query.order(orderColumn, { ascending: true });
  const rows: Row[] = [];
  let from = 0;

  for (;;) {
    const { data, error } = await ordered.range(from, from + POSTGREST_MAX_ROWS - 1);
    if (error) throw error;

    const page = data ?? [];
    rows.push(...page);

    if (page.length < POSTGREST_MAX_ROWS) return rows;
    from += POSTGREST_MAX_ROWS;
  }
}
